import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";

import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import MilestoneOfferForm from "../milestone";
import RecurringOfferForm from "../recurring";
import FullTimeOfferForm from "../full-time";

import { useMakeCounterOffer } from "@/mutations/opportunity/counter-offer";

import {
  CandidateStatus,
  JobOpening,
  MinimalCandidateStatus,
  NewOffer,
  Offer,
} from "@/types";

enum MODE {
  RECURRING = "RECURRING",
  MILESTONE = "MILESTONE",
  FULLTIME = "FULLTIME",
}

const OpportunityCounterOfferForm = ({
  jobOpening,
  candidate,
  offer,
  onCancel,
  onSuccess,
}: {
  jobOpening: JobOpening;
  candidate?: CandidateStatus | MinimalCandidateStatus;
  offer: Offer;
  onCancel: () => void;
  onSuccess?: () => void;
}) => {
  const [mode, setMode] = useState<MODE | undefined>();
  const [modeChoice, setModeChoice] = useState<MODE | undefined>();

  useEffect(() => {
    if (jobOpening.type === "Fractional") {
      setMode(MODE.RECURRING);
      setModeChoice(undefined);
    } else if (jobOpening.type === "Full-time") {
      setMode(MODE.FULLTIME);
      setModeChoice(undefined);
    }
  }, [jobOpening]);

  const {
    makeCounterOffer,
    isMakingCounterOffer: isPending,
    counterOfferError: mutationError,
  } = useMakeCounterOffer(onSuccess);

  function onSubmit(data: NewOffer) {
    if (isPending) return;
    makeCounterOffer({
      openingId: jobOpening.id,
      offerId: offer.id,
      offer: data,
    });
  }

  if (!mode) {
    return (
      <div className="flex flex-col gap-6 items-center">
        <div className="flex flex-col gap-2">
          <Label className="text-base text-[#2E2E38] font-normal" required>
            Offer Type
          </Label>
          <RadioGroup
            value={modeChoice}
            onValueChange={(data) => setModeChoice(data as MODE)}
          >
            <RadioGroupItem
              variant="radioBar"
              label="Time-based"
              description="Recurring monthly fee for ongoing work over an agreed engagement period."
              value={MODE.RECURRING}
            />
            <RadioGroupItem
              variant="radioBar"
              label="Milestone-based"
              description="Payment is released as predefined milestones are completed and approved."
              value={MODE.MILESTONE}
            />
          </RadioGroup>
        </div>
        <div className="h-[1px] bg-[#E4E7EC] w-full" />
        <div className={cn("w-full flex flex-row justify-end")}>
          <Button
            variant="success"
            disabled={!modeChoice}
            onClick={() => setMode(modeChoice)}
          >
            Continue
          </Button>
        </div>
      </div>
    );
  }

  return mode === MODE.MILESTONE ? (
    <MilestoneOfferForm
      sender={offer.sender === "candidate" ? "company" : "candidate"}
      candidateName={
        candidate && "name" in candidate
          ? candidate.name?.split(" ")[0]
          : undefined
      }
      isPending={isPending}
      error={mutationError}
      isCounter
      mutate={onSubmit}
      onCancel={onCancel}
    />
  ) : mode === MODE.RECURRING ? (
    <RecurringOfferForm
      sender={offer.sender === "candidate" ? "company" : "candidate"}
      isPending={isPending}
      error={mutationError}
      mutate={onSubmit}
      onCancel={onCancel}
    />
  ) : (
    <FullTimeOfferForm
      sender={offer.sender === "candidate" ? "company" : "candidate"}
      isPending={isPending}
      error={mutationError}
      mutate={onSubmit}
      onCancel={onCancel}
    />
  );
};

export default OpportunityCounterOfferForm;
